import { DOCUMENT } from '@angular/common';
import {
    Component,
    OnInit,
    OnDestroy,
    Inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, map } from 'rxjs';
import {
    delay, distinctUntilChanged, filter, takeUntil, first,
} from 'rxjs/operators';

import { applicationMyAccountPagesMap } from '@core/domain/constants/application-pages.const';
import { PageSection } from '@core/domain/types/page-sections.types';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getCmsConfigValue, getPage } from '@core/infrastructure/state/core.selectors';
import { isHomePage } from '@features/category/application/services/category.utils';
import { CmsRenderLazyComponent } from '@features/cms-render';
import { checkMatch } from '@frontend/common/utils';
import { OffsetBodyDirective } from '@layout/ui/directives/offset-body.directive';
import { getCategoryId } from '@pages/category';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';

@Component({
    selector: 'app-top-notification',
    templateUrl: './top-notification.component.html',
    standalone: true,
    imports: [
        OffsetBodyDirective,
        CmsRenderLazyComponent,
    ],
})
export class TopNotificationComponent implements OnInit, OnDestroy {
    public slugNotification;

    private readonly onDestroy$ = new Subject();

    public getPage$ = this.store.select(getPage);

    public getCategoryNotification$ = this.store
        .select(getCmsConfigValue('category_grid_notification_ids')).pipe(
            map((categoryGridNotificationIds) => {
                if (Array.isArray(categoryGridNotificationIds)) {
                    return categoryGridNotificationIds?.map(i => i.toString());
                }
                return [];
            })
        );

    public categoryId$ = this.store.select(getCategoryId).pipe(map(categoryId => categoryId?.toString()));

    constructor(
        private readonly store: Store,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly abTestingService: ABTestingService,
        private readonly platform: CorePlatformService
    ) {}

    public ngOnInit() {
        if (this.platform.isBrowser()) {
            this.initNotificationSystem();
        }
    }

    private initNotificationSystem(): void {
        combineLatest([
            this.getPage$,
            this.getCategoryNotification$,
            this.categoryId$,
        ])
            .pipe(
                distinctUntilChanged(),
                filter(([page]) => !!page),
                delay(50),
                takeUntil(this.onDestroy$)
            )
            .subscribe(([page, notificationIds, categoryId]) => {
                this.handleNotification(page, notificationIds, categoryId);
            });
    }

    private handleNotification(
        page: PageSection,
        notificationIds: string[],
        categoryId: string
    ): void {
        this.slugNotification = this.getSlugByPage(page, notificationIds, categoryId);

        if (isHomePage(page)) {
            this.document.body.style.setProperty('--offset-body', '0px');
        }

        this.setTestAbSystem(page, notificationIds, categoryId);
    }

    private getSlugByPage(page: PageSection, notificationIds: string[], categoryId: string): string {
        switch (page) {
            case 'ItxHomePage': return 'mspot-web-notification-women';
            case 'ItxHomeWomenPage': return 'mspot-web-notification-women';
            case 'ItxHomeMenPage': return 'mspot-web-notification-men';
            case 'ItxHomeBgPage': return 'mspot-web-notification-limited';
            case 'ItxCategoryPage':
                if (notificationIds.length === 0) {
                    return 'mspot-web-notification-category';
                }
                return notificationIds.includes(categoryId) ? 'mspot-web-notification-category' : '';
            case 'ItxProductPage': return 'mspot-web-notification-category';
            case 'ItxAngularShopcartPage': return 'mspot-web-notification-shopcart';
            default: break;
        }

        if (checkMatch(page, applicationMyAccountPagesMap)) {
            return 'mspot-web-notification-myaccount';
        }
        return '';
    }

    private setTestAbSystem(page: PageSection, notificationIds: string[], categoryId: string): void {
        this.abTestingService.showInformativeMsg$
            .pipe(filter(Boolean), first())
            .subscribe(() => {
                this.slugNotification = `${this.getSlugByPage(page, notificationIds, categoryId)}-test`;
            });
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next(null);
        this.onDestroy$.complete();
    }
}
